import { useState, useEffect } from 'react';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown,
  Wifi, Radio, Building2
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';
import SelectWithCreate from '../../components/ui/select-with-create';

const AccessPoints = () => {
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('assets', 'access_points');
  const [accessPoints, setAccessPoints] = useState([]);
  const [stats, setStats] = useState({ total: 0, ubiquiti: 0, cisco: 0, aruba: 0, otros: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', site: '', marca: '', status_id: '' });
  const [searchInput, setSearchInput] = useState(''); // Input inmediato para debounce
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const [pageSize, setPageSize] = useState(25);

  // Form options
  const [options, setOptions] = useState({ modelos: [], sites: [], ubicaciones: [], proveedores: [], estados: [] });
  
  // Filter options (only values that exist in access_points table)
  const [filterOptions, setFilterOptions] = useState({ marcas: [], sites: [], estados: [] });

  // Modals
  const [viewModal, setViewModal] = useState({ open: false, ap: null });
  const [editModal, setEditModal] = useState({ open: false, ap: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, ap: null });
  const [formData, setFormData] = useState({
    name: '', ip: '', mac_address: '', ubicacion: '', modelo: '', site: '', status_id: '',
    nro_serie: '', firmware: '', proveedor: '', costo: '', fecha_compra: '', observations: ''
  });
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/access-points/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchOptions = async () => {
    try {
      const response = await api.get('/assets/access-points/form/options');
      setOptions(response.data);
    } catch (error) {
      console.error('Error fetching options:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/access-points/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchAccessPoints = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.site) params.append('site', filters.site);
      if (filters.marca) params.append('marca', filters.marca);
      if (filters.status_id) params.append('status_id', filters.status_id);

      const response = await api.get(`/assets/access-points?${params}`);
      setAccessPoints(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar access points');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchOptions();
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchAccessPoints();
  }, [page, pageSize, filters.search, filters.site, filters.marca, filters.status_id, sortConfig]);

  // Debounce para filtro de búsqueda (800ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters(prev => ({ ...prev, search: searchInput }));
        setPage(1);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Marca badge
  const getMarcaBadge = (marca) => {
    if (!marca) return <span className="badge badge-outline">Sin modelo</span>;
    const m = marca.toLowerCase();
    if (m.includes('ubiquiti')) return <span className="badge badge-info">Ubiquiti</span>;
    if (m.includes('cisco')) return <span className="badge badge-success">Cisco</span>;
    if (m.includes('aruba')) return <span className="badge badge-warning">Aruba</span>;
    if (m.includes('tp-link')) return <span className="badge badge-secondary">TP-Link</span>;
    return <span className="badge badge-outline">{marca}</span>;
  };

  // View modal
  const handleView = (ap) => {
    setViewModal({ open: true, ap });
  };

  // Edit modal
  const handleEdit = (ap) => {
    setFormData({
      name: ap.name || '',
      ip: ap.ip || '',
      mac_address: ap.mac_address || '',
      ubicacion: ap.ubicacion?.toString() || '',
      modelo: ap.modelo?.toString() || '',
      site: ap.site?.toString() || '',
      status_id: ap.status_id?.toString() || '',
      nro_serie: ap.nro_serie || '',
      firmware: ap.firmware || '',
      proveedor: ap.proveedor?.toString() || '',
      costo: ap.costo?.toString() || '',
      fecha_compra: ap.fecha_compra || '',
      observations: ap.observations || ''
    });
    setEditModal({ open: true, ap, isCopy: false });
  };

  // Copy modal
  const handleCopy = (ap) => {
    setFormData({
      name: '', // Unique - clear
      ip: '', // Unique - clear
      mac_address: '', // Unique - clear
      ubicacion: ap.ubicacion?.toString() || '', // Copy
      modelo: ap.modelo?.toString() || '', // Copy
      site: ap.site?.toString() || '', // Copy
      status_id: ap.status_id?.toString() || '', // Copy
      nro_serie: '', // Unique - clear
      firmware: ap.firmware || '', // Copy
      proveedor: ap.proveedor?.toString() || '', // Copy
      costo: ap.costo?.toString() || '', // Copy
      fecha_compra: '', // Clear
      observations: ap.observations || '' // Copy
    });
    setEditModal({ open: true, ap: null, isCopy: true });
  };

  // Create new
  const handleCreate = () => {
    setFormData({
      name: '', ip: '', mac_address: '', ubicacion: '', modelo: '', site: '', status_id: '',
      nro_serie: '', firmware: '', proveedor: '', costo: '', fecha_compra: '', observations: ''
    });
    setEditModal({ open: true, ap: null, isCopy: false });
  };

  // Save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        ubicacion: formData.ubicacion ? parseInt(formData.ubicacion) : null,
        modelo: formData.modelo ? parseInt(formData.modelo) : null,
        site: formData.site ? parseInt(formData.site) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        proveedor: formData.proveedor ? parseInt(formData.proveedor) : null,
        costo: formData.costo ? parseFloat(formData.costo) : null,
        fecha_compra: formData.fecha_compra || null
      };

      if (editModal.ap && !editModal.isCopy) {
        await api.put(`/assets/access-points/${editModal.ap.id}`, payload);
        toast.success('Access Point actualizado exitosamente');
      } else {
        await api.post('/assets/access-points', payload);
        toast.success('Access Point creado exitosamente');
      }
      setEditModal({ open: false, ap: null, isCopy: false });
      fetchAccessPoints();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await api.delete(`/assets/access-points/${deleteModal.ap.id}`);
      toast.success('Access Point eliminado exitosamente');
      setDeleteModal({ open: false, ap: null });
      fetchAccessPoints();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Quick create modelo
  const handleQuickCreateModelo = async (nombre, extraData) => {
    // Find marca_id from marca name
    let marca_id = null;
    if (extraData?.marca) {
      const foundMarca = options.modelos.find(m => m.marca === extraData.marca);
      if (foundMarca) {
        // Get marca_id from an existing model with this brand
        // We need to fetch marcas to get the ID
        const marcasResponse = await api.get('/config/modelos-ap/marcas');
        const marca = marcasResponse.data.find(m => m.nombre === extraData.marca);
        if (marca) {
          marca_id = marca.id;
        }
      }
    }
    
    const response = await api.post('/assets/access-points/quick-create/modelo', { 
      nombre,
      marca_id
    });
    await fetchOptions();
    return response.data;
  };

  // Get unique marcas for filter
  const uniqueMarcas = [...new Set(options.modelos.map(m => m.marca).filter(Boolean))];

  if (!canRead) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">No tienes permisos para ver Access Points</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Access Points</h1>
            <p className="page-subtitle">Gestion de puntos de acceso inalambricos</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/access-points"
              filters={{ search: filters.search }}
              filename="access_points"
            />
            {canCreate && (
              <Button onClick={handleCreate} className="btn btn-primary" data-testid="new-ap-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuevo AP
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ap-stats">
          <div className="stats-card flex items-center gap-3" data-testid="stats-total">
            <Wifi className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-ubiquiti">
            <Radio className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.ubiquiti}</div>
              <div className="stats-card-label">Ubiquiti</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-cisco">
            <Radio className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.cisco}</div>
              <div className="stats-card-label">Cisco</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-aruba">
            <Radio className="h-6 w-6 text-orange-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-orange-500">{stats.aruba}</div>
              <div className="stats-card-label">Aruba</div>
            </div>
          </div>
          <div className="stats-card flex items-center gap-3" data-testid="stats-otros">
            <Radio className="h-6 w-6 text-gray-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-gray-500">{stats.otros}</div>
              <div className="stats-card-label">Otros</div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card className="card-subtle overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="table-subtle">
                    <thead>
                      <tr>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center gap-1">
                            NOMBRE
                            <SortIcon field="name" />
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('ip')}
                        >
                          <div className="flex items-center gap-1">
                            IP
                            <SortIcon field="ip" />
                          </div>
                        </th>
                        <th>MAC</th>
                        <th className="text-center">MARCA</th>
                        <th>MODELO</th>
                        <th>SITE</th>
                        <th 
                          className="cursor-pointer hover:bg-muted/50 select-none"
                          onClick={() => handleSort('status_name')}
                        >
                          <div className="flex items-center gap-1">
                            ESTADO
                            <SortIcon field="status_name" />
                          </div>
                        </th>
                        <th className="w-28 text-center">ACCIONES</th>
                      </tr>
                      <tr className="bg-muted/30">
                        <th className="py-2 px-4">
                          <Input
                            placeholder="Buscar en todos los campos..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            className="h-7 text-xs bg-background"
                            data-testid="filter-search"
                          />
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.marca || undefined}
                            onValueChange={(v) => handleFilterChange('marca', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todas</SelectItem>
                              {filterOptions.marcas?.map(marca => (
                                <SelectItem key={marca.id} value={marca.nombre}>{marca.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4"></th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.site || undefined}
                            onValueChange={(v) => handleFilterChange('site', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.sites?.map(site => (
                                <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="py-2 px-4">
                          <Select
                            value={filters.status_id || undefined}
                            onValueChange={(v) => handleFilterChange('status_id', v === "todos" ? "" : v)}
                          >
                            <SelectTrigger className="h-7 text-xs bg-background">
                              <SelectValue placeholder="Todos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todos">Todos</SelectItem>
                              {filterOptions.estados?.map(e => (
                                <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </th>
                        <th className="w-28"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessPoints.length === 0 ? (
                        <tr>
                          <td colSpan={8}>
                            <div className="empty-state">
                              <Wifi className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                              <p className="empty-state-text">No se encontraron access points</p>
                            </div>
                          </td>
                        </tr>
                      ) : accessPoints.map((ap) => (
                        <tr key={ap.id} data-testid={`ap-row-${ap.id}`}>
                          <td className="font-medium text-primary">{ap.name}</td>
                          <td className="font-mono text-sm text-muted-foreground">{ap.ip || '-'}</td>
                          <td className="font-mono text-xs text-muted-foreground">{ap.mac_address || '-'}</td>
                          <td className="text-center">{getMarcaBadge(ap.marca)}</td>
                          <td className="text-muted-foreground">{ap.modelo_nombre || '-'}</td>
                          <td className="text-muted-foreground">{ap.site_nombre || '-'}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              ap.status_name?.toLowerCase().includes('activo') 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : ap.status_name?.toLowerCase().includes('baja') || ap.status_name?.toLowerCase().includes('inactivo')
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {ap.status_name || 'Sin estado'}
                            </span>
                          </td>
                          <td className="w-28">
                            <div className="flex justify-center gap-0.5">
                              <button
                                onClick={() => handleView(ap)}
                                className="action-btn"
                                title="Ver detalle"
                                data-testid={`view-ap-${ap.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canUpdate && handleEdit(ap)}
                                className={`action-btn action-btn-edit ${!canUpdate ? 'opacity-50' : ''}`}
                                disabled={!canUpdate}
                                title="Editar"
                                data-testid={`edit-ap-${ap.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canCreate && handleCopy(ap)}
                                className={`action-btn action-btn-copy ${!canCreate ? 'opacity-50' : ''}`}
                                disabled={!canCreate}
                                title="Copiar"
                                data-testid={`copy-ap-${ap.id}`}
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => canDelete && setDeleteModal({ open: true, ap })}
                                className={`action-btn action-btn-delete ${!canDelete ? 'opacity-50' : ''}`}
                                disabled={!canDelete}
                                title="Eliminar"
                                data-testid={`delete-ap-${ap.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      {total > 0 ? `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}` : '0 registros'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Mostrar:</span>
                      <Select
                        value={pageSize.toString()}
                        onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                      >
                        <SelectTrigger className="h-8 w-20">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* View Modal */}
        <Dialog open={viewModal.open} onOpenChange={(open) => setViewModal({ open, ap: null })}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-primary" />
                {viewModal.ap?.name}
              </DialogTitle>
            </DialogHeader>
            {viewModal.ap && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Nombre</Label>
                    <p className="font-medium">{viewModal.ap.name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">IP</Label>
                    <p className="font-mono">{viewModal.ap.ip || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">MAC Address</Label>
                    <p className="font-mono text-sm">{viewModal.ap.mac_address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Modelo</Label>
                    <div className="mt-1">{getMarcaBadge(viewModal.ap.marca)}</div>
                    <p className="text-sm">{viewModal.ap.modelo_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Firmware</Label>
                    <p className="font-mono text-sm">{viewModal.ap.firmware || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Nro Serie</Label>
                    <p className="font-mono text-sm">{viewModal.ap.nro_serie || '-'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Site</Label>
                    <p>{viewModal.ap.site_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ubicacion</Label>
                    <p>{viewModal.ap.ubicacion_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Proveedor</Label>
                    <p>{viewModal.ap.proveedor_nombre || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Costo</Label>
                    <p>{viewModal.ap.costo ? `$${viewModal.ap.costo.toLocaleString()}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fecha Compra</Label>
                    <p>{viewModal.ap.fecha_compra || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Observaciones</Label>
                    <p className="text-sm text-muted-foreground">{viewModal.ap.observations || '-'}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit/Create/Copy Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, ap: null, isCopy: false })}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wifi className="h-5 w-5 text-primary" />
                {editModal.isCopy ? 'Copiar Access Point' : editModal.ap ? 'Editar Access Point' : 'Nuevo Access Point'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Unique fields */}
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Nombre del AP"
                  data-testid="input-name"
                  data-validation={editModal.isCopy ? (!formData.name ? 'required-empty' : '') : (!formData.name ? 'required-empty' : 'valid')}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">IP</Label>
                <Input
                  value={formData.ip}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip: e.target.value }))}
                  placeholder="192.168.1.100"
                  data-testid="input-ip"
                  data-validation={editModal.isCopy ? (!formData.ip ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">MAC Address</Label>
                <Input
                  value={formData.mac_address}
                  onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF"
                  data-testid="input-mac"
                  data-validation={editModal.isCopy ? (!formData.mac_address ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Nro Serie</Label>
                <Input
                  value={formData.nro_serie}
                  onChange={(e) => setFormData(prev => ({ ...prev, nro_serie: e.target.value }))}
                  placeholder="Serial number"
                  data-testid="input-serie"
                  data-validation={editModal.isCopy ? (!formData.nro_serie ? 'required-empty' : '') : ''}
                />
                {editModal.isCopy && <p className="text-xs text-yellow-600">Campo unico</p>}
              </div>

              {/* Copy fields */}
              <div className="space-y-1">
                <Label className="text-xs">Modelo</Label>
                <SelectWithCreate
                  value={formData.modelo}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, modelo: v }))}
                  options={options.modelos}
                  placeholder="Seleccionar modelo"
                  onCreate={handleQuickCreateModelo}
                  createLabel="modelo"
                  showExtraField={true}
                  extraFieldLabel="Marca"
                  extraFieldKey="marca"
                  data-validation={editModal.isCopy && formData.modelo ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Site</Label>
                <Select
                  value={formData.site || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, site: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-site" data-validation={editModal.isCopy && formData.site ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar site" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin site</SelectItem>
                    {options.sites.map(site => (
                      <SelectItem key={site.id} value={site.id.toString()}>{site.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Ubicacion</Label>
                <Select
                  value={formData.ubicacion || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, ubicacion: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-ubicacion" data-validation={editModal.isCopy && formData.ubicacion ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar ubicacion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin ubicacion</SelectItem>
                    {options.ubicaciones.map(ub => (
                      <SelectItem key={ub.id} value={ub.id.toString()}>{ub.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select
                  value={formData.status_id || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-status" data-validation={editModal.isCopy && formData.status_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin estado</SelectItem>
                    {options.estados?.map(e => (
                      <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Firmware</Label>
                <Input
                  value={formData.firmware}
                  onChange={(e) => setFormData(prev => ({ ...prev, firmware: e.target.value }))}
                  placeholder="Version de firmware"
                  data-testid="input-firmware"
                  data-validation={editModal.isCopy && formData.firmware ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Proveedor</Label>
                <Select
                  value={formData.proveedor || "none"}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, proveedor: v === "none" ? "" : v }))}
                >
                  <SelectTrigger data-testid="select-proveedor" data-validation={editModal.isCopy && formData.proveedor ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin proveedor</SelectItem>
                    {options.proveedores.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Costo</Label>
                <Input
                  type="number"
                  value={formData.costo}
                  onChange={(e) => setFormData(prev => ({ ...prev, costo: e.target.value }))}
                  placeholder="0.00"
                  data-testid="input-costo"
                  data-validation={editModal.isCopy && formData.costo ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs">Fecha Compra</Label>
                <Input
                  type="date"
                  value={formData.fecha_compra}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha_compra: e.target.value }))}
                  data-testid="input-fecha"
                />
              </div>
              
              <div className="col-span-2 space-y-1">
                <Label className="text-xs">Observaciones</Label>
                <Textarea
                  value={formData.observations}
                  onChange={(e) => setFormData(prev => ({ ...prev, observations: e.target.value }))}
                  placeholder="Notas adicionales..."
                  rows={2}
                  data-testid="input-observations"
                  data-validation={editModal.isCopy && formData.observations ? 'valid' : ''}
                />
                {editModal.isCopy && <p className="text-xs text-green-600">Copiado</p>}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, ap: null, isCopy: false })}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving} className="btn btn-primary">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, ap: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Eliminar Access Point
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              ¿Estas seguro que deseas eliminar el AP <strong>{deleteModal.ap?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, ap: null })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AccessPoints;
